import React, { useEffect, useState, Suspense, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { TravelModeProvider } from '../contexts/TravelModeContext';
import { LoadingScreen } from '../components/LoadingScreen';
import { motion } from 'framer-motion';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  ChevronDown, 
  Building2, 
  Heart, 
  Star, 
  Award,
  Search as SearchIcon,
  Home as HomeIcon,
  MapPin,
  Grid,
  PlusCircle,
  Shield,
  FileText,
  MessageSquare,
  Info,
  HelpCircle,
  Mail,
  Handshake,
  AlertTriangle
} from 'lucide-react';

console.log("VITE_APP_MODE:", import.meta.env.VITE_APP_MODE);

// Lazy load web pages
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));
const VerificationProcess = React.lazy(() => import('./pages/VerificationProcess'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const Search = React.lazy(() => import('./pages/Search'));
const Profile = React.lazy(() => import('./pages/Profile'));
const ProfileSettings = React.lazy(() => import('./pages/ProfileSettings'));

// Import app pages
const BusinessLanding = React.lazy(() => import('../pages/static/BusinessLanding'));
const Categories = React.lazy(() => import('../pages/Categories'));
const CategoryPage = React.lazy(() => import('../pages/CategoryPage'));
const ListingDetail = React.lazy(() => import('../pages/ListingDetail'));
const Submit = React.lazy(() => import('../pages/Submit'));
const Favorites = React.lazy(() => import('../pages/Favorites'));
const AppProfile = React.lazy(() => import('../pages/profile/Profile'));
const WriteReview = React.lazy(() => import('../pages/WriteReview'));

// Static pages
const OurStory = React.lazy(() => import('../pages/static/OurStory'));
const Partner = React.lazy(() => import('../pages/static/Partner'));
const Verified = React.lazy(() => import('../pages/static/Verified'));
const VerificationForm = React.lazy(() => import('../pages/static/VerificationForm'));
const Report = React.lazy(() => import('../pages/static/Report'));
const JoinTeam = React.lazy(() => import('../pages/static/JoinTeam'));
const AmbassadorApplication = React.lazy(() => import('../pages/static/AmbassadorApplication'));
const BusinessTiers = React.lazy(() => import('../pages/static/BusinessTiers'));
const Disclaimer = React.lazy(() => import('../pages/static/Disclaimer'));
const Terms = React.lazy(() => import('../pages/static/Terms'));
const Privacy = React.lazy(() => import('../pages/static/Privacy'));

// Onboarding pages
const Welcome = React.lazy(() => import('../pages/onboarding/Welcome'));
const AllergyPreferences = React.lazy(() => import('../pages/onboarding/AllergyPreferences'));
const Location = React.lazy(() => import('../pages/onboarding/Location'));
const OnboardingSignup = React.lazy(() => import('../pages/onboarding/Signup'));
const Success = React.lazy(() => import('../pages/onboarding/Success'));

// Error Boundary Component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">We're sorry, but there was an error loading this page.</p>
            <button 
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }} 
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Try reloading the page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ScrollToTop component to handle scroll behavior
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

// Web Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  
  // Refs for dropdown containers
  const businessDropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // Use a ref to store the scroll handler
  const handleScroll = () => {
    const isScrolled = window.scrollY > 10;
    if (isScrolled !== scrolled) {
      setScrolled(isScrolled);
    }
  };

  useEffect(() => {
    // Add event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Call once to initialize
    handleScroll();
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]); // Only depend on scrolled state

  // Add click outside listener to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close business dropdown if click is outside
      if (
        isDropdownOpen && 
        businessDropdownRef.current && 
        !businessDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
      
      // Close user dropdown if click is outside
      if (
        isUserDropdownOpen && 
        userDropdownRef.current && 
        !userDropdownRef.current.contains(event.target as Node)
      ) {
        setIsUserDropdownOpen(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isUserDropdownOpen]);

  return (
    <header className={`sticky top-0 left-0 right-0 z-50 bg-white ${scrolled ? 'shadow-md' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-bold text-primary-600">Nut Free Nomad</span>
              </Link>
            </div>
            <nav className="hidden md:ml-6 md:flex md:space-x-8">
              <Link to="/" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300">
                <HomeIcon className="w-4 h-4 mr-1" />
                Home
              </Link>
              <Link to="/search" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300">
                <SearchIcon className="w-4 h-4 mr-1" />
                Search
              </Link>
              <Link to="/categories" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300">
                <Grid className="w-4 h-4 mr-1" />
                Categories
              </Link>
              <Link to="/submit" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300">
                <PlusCircle className="w-4 h-4 mr-1" />
                Submit
              </Link>
              <div className="relative" ref={businessDropdownRef}>
                <button
                  onClick={() => {
                    setIsDropdownOpen(!isDropdownOpen);
                    // Close other dropdown if open
                    if (isUserDropdownOpen) setIsUserDropdownOpen(false);
                  }}
                  className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
                >
                  <Info className="w-4 h-4 mr-1" />
                  About
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>
                {isDropdownOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <Link to="/our-story" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Our Story
                    </Link>
                    <Link to="/contact" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Contact Us
                    </Link>
                    <Link to="/partner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Partner With Us
                    </Link>
                    <Link to="/verified" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Become Verified
                    </Link>
                    <Link to="/report" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Report a Location
                    </Link>
                    <Link to="/join-team" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Join Our Team
                    </Link>
                  </div>
                )}
              </div>
              <a 
                href="https://biz.nutfreenomad.com" 
                className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
              >
                <Building2 className="w-4 h-4 mr-1" />
                For Businesses
              </a>
            </nav>
          </div>
          <div className="hidden md:ml-6 md:flex md:items-center">
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link to="/favorites" className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300">
                  <Heart className="w-4 h-4 mr-1" />
                  Favorites
                </Link>
                <div className="relative" ref={userDropdownRef}>
                  <button
                    onClick={() => {
                      setIsUserDropdownOpen(!isUserDropdownOpen);
                      // Close other dropdown if open
                      if (isDropdownOpen) setIsDropdownOpen(false);
                    }}
                    className="flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="h-8 w-8 rounded-full bg-primary-200 flex items-center justify-center text-primary-800 font-bold">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  </button>
                  {isUserDropdownOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Your Profile
                      </Link>
                      <Link to="/favorites" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Favorites
                      </Link>
                      <button
                        onClick={() => {
                          // Sign out logic
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-white hover:bg-gray-50">
                  Log in
                </Link>
                <Link 
                  to="/signup" 
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white">
          <div className="pt-2 pb-3 space-y-1">
            <Link to="/" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <HomeIcon className="w-5 h-5 mr-2" />
                Home
              </div>
            </Link>
            <Link to="/search" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <SearchIcon className="w-5 h-5 mr-2" />
                Search
              </div>
            </Link>
            <Link to="/categories" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <Grid className="w-5 h-5 mr-2" />
                Categories
              </div>
            </Link>
            <Link to="/submit" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <PlusCircle className="w-5 h-5 mr-2" />
                Submit
              </div>
            </Link>
            <Link to="/favorites" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <Heart className="w-5 h-5 mr-2" />
                Favorites
              </div>
            </Link>
            <Link to="/our-story" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <Info className="w-5 h-5 mr-2" />
                Our Story
              </div>
            </Link>
            <Link to="/contact" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Contact Us
              </div>
            </Link>
            <Link to="/partner" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <Handshake className="w-5 h-5 mr-2" />
                Partner With Us
              </div>
            </Link>
            <Link to="/verified" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Become Verified
              </div>
            </Link>
            <Link to="/report" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Report a Location
              </div>
            </Link>
            <a 
              href="https://biz.nutfreenomad.com" 
              className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
            >
              <div className="flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                For Businesses
              </div>
            </a>
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            {isAuthenticated ? (
              <div>
                <div className="flex items-center px-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-primary-200 flex items-center justify-center text-primary-800 font-bold">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800">{user?.email}</div>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <Link to="/profile" className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                    Your Profile
                  </Link>
                  <Link to="/favorites" className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                    Favorites
                  </Link>
                  <button
                    onClick={() => {
                      // Sign out logic
                    }}
                    className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col space-y-2 px-4">
                <Link to="/login" className="block text-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-50">
                  Log in
                </Link>
                <Link 
                  to="/signup" 
                  className="block text-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

// Web Footer Component
const Footer = () => {
  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Company</h3>
            <ul className="mt-4 space-y-4">
              <li>
                <Link to="/our-story" className="text-base text-gray-500 hover:text-gray-900">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-base text-gray-500 hover:text-gray-900">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/join-team" className="text-base text-gray-500 hover:text-gray-900">
                  Careers
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Resources</h3>
            <ul className="mt-4 space-y-4">
              <li>
                <Link to="/submit" className="text-base text-gray-500 hover:text-gray-900">
                  Submit a Place
                </Link>
              </li>
              <li>
                <Link to="/partner" className="text-base text-gray-500 hover:text-gray-900">
                  Partner With Us
                </Link>
              </li>
              <li>
                <Link to="/verified" className="text-base text-gray-500 hover:text-gray-900">
                  Verification Process
                </Link>
              </li>
              <li>
                <Link to="/report" className="text-base text-gray-500 hover:text-gray-900">
                  Report a Location
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">For Businesses</h3>
            <ul className="mt-4 space-y-4">
              <li>
                <a href="https://biz.nutfreenomad.com/pricing" className="text-base text-gray-500 hover:text-gray-900">
                  Pricing
                </a>
              </li>
              <li>
                <a href="https://biz.nutfreenomad.com/verification" className="text-base text-gray-500 hover:text-gray-900">
                  Verification Process
                </a>
              </li>
              <li>
                <a href="https://biz.nutfreenomad.com" className="text-base text-gray-500 hover:text-gray-900">
                  Business Dashboard
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Legal</h3>
            <ul className="mt-4 space-y-4">
              <li>
                <Link to="/terms" className="text-base text-gray-500 hover:text-gray-900">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-base text-gray-500 hover:text-gray-900">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-base text-gray-500 hover:text-gray-900">
                  Disclaimer
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-12 border-t border-gray-200 pt-8">
          <p className="text-base text-gray-400 text-center">
            &copy; {new Date().getFullYear()} Nut Free Nomad. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Mobile Bottom Navigation
const MobileNav = () => {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2 md:hidden z-40">
      <div className="grid grid-cols-5 gap-1">
        <Link to="/" className="flex flex-col items-center justify-center">
          <HomeIcon className="w-6 h-6 text-gray-500" />
          <span className="text-xs mt-1 text-gray-500">Home</span>
        </Link>
        <Link to="/search" className="flex flex-col items-center justify-center">
          <SearchIcon className="w-6 h-6 text-gray-500" />
          <span className="text-xs mt-1 text-gray-500">Search</span>
        </Link>
        <Link to="/submit" className="flex flex-col items-center justify-center">
          <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center -mt-5">
            <PlusCircle className="w-7 h-7 text-white" />
          </div>
          <span className="text-xs mt-1 text-gray-500">Submit</span>
        </Link>
        <Link to="/favorites" className="flex flex-col items-center justify-center">
          <Heart className="w-6 h-6 text-gray-500" />
          <span className="text-xs mt-1 text-gray-500">Favorites</span>
        </Link>
        <Link to="/profile" className="flex flex-col items-center justify-center">
          <User className="w-6 h-6 text-gray-500" />
          <span className="text-xs mt-1 text-gray-500">Profile</span>
        </Link>
      </div>
    </div>
  );
};

function AppRoutes() {
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      <div className="flex-1 pb-16 md:pb-0">
        <ScrollToTop />
        <Suspense fallback={<LoadingScreen />}>
          <ErrorBoundary>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/our-story" element={<OurStory />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/verification" element={<VerificationProcess />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/business/:slug" element={<ListingDetail />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/:category" element={<CategoryPage />} />
              <Route path="/search" element={<Search />} />
              <Route path="/partner" element={<Partner />} />
              <Route path="/verified" element={<Verified />} />
              <Route path="/verified/apply" element={<VerificationForm />} />
              <Route path="/report" element={<Report />} />
              <Route path="/join-team" element={<JoinTeam />} />
              <Route path="/join-team/apply" element={<AmbassadorApplication />} />
              <Route path="/business-tiers" element={<BusinessTiers />} />
              
              {/* Onboarding routes */}
              <Route path="/onboarding" element={<Welcome />} />
              <Route path="/onboarding/allergies" element={<AllergyPreferences />} />
              <Route path="/onboarding/location" element={<Location />} />
              <Route path="/onboarding/signup" element={<OnboardingSignup />} />
              <Route path="/onboarding/success" element={<Success />} />
              
              {/* Redirect business signup to business domain */}
              <Route path="/signup" element={<Signup />} />
              
              {/* Protected routes */}
              <Route path="/submit" element={<ProtectedRoute><Submit /></ProtectedRoute>} />
              <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/profile/settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
              <Route path="/write-review/:listingId" element={<ProtectedRoute><WriteReview /></ProtectedRoute>} />
              
              {/* Catch all route */}
              <Route path="*" element={
                <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                  <div className="text-center">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Page not found</h2>
                    <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
                      Return home
                    </Link>
                  </div>
                </div>
              } />
            </Routes>
          </ErrorBoundary>
        </Suspense>
      </div>
      <MobileNav />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <TravelModeProvider>
          <AppRoutes />
        </TravelModeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;